import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import AddIcon from 'transfix-icons/build/AddIcon'
import EditIcon from 'transfix-icons/build/EditIcon'
import EllipsisIcon from 'transfix-icons/build/EllipsisIcon'
import { Button } from 'transfix-ui/components/Button'
import { Tabs, Tab } from 'transfix-ui/components/DeprecatedTabs'
import { IconButton } from 'transfix-ui/components/IconButton'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { OverflowMenu, useOverflowToggle } from 'transfix-ui/components/OverflowMenu'
import { Spinner } from 'transfix-ui/components/Spinner'
import { $enum } from 'ts-enum-util'
import { ShipperRatesFilter } from 'apps/rms/components/ShipperRates/ShipperRatesFilter'
import { ShipperRatesTable } from 'apps/rms/components/ShipperRates/ShipperRatesTable'
import { SHIPPER_RATE_STATUS } from 'apps/rms/constants/shipperRates'
import {
  SORT_ORDER,
  SORT_FIELD,
  IShipperRateModalPayload,
  IShipperRatesParams,
  IShipperRate,
  IActiveStatusCounts,
} from 'apps/rms/types/shipperRates'
import {
  fetchShipperRates,
  openEditShipperRateModal,
  setActiveTab,
} from 'apps/rms/utils/actions/shipperRates'
import { IStore } from 'apps/rms/utils/reducers'
import { IDropdownOption } from 'components/forms'
import { Container } from 'components/layout'
import { capitalize } from 'utils/capitalize'
import { useModal } from 'utils/hooks'
import { BulkRatesModal } from './BulkRatesModal'
import { EditShipperRateModal } from './EditShipperRateModal'
import { ShipperRateHistory } from './ShipperRateHistory'
import { UpdateAcceptanceCapModal } from './UpdateAcceptanceCapModal'

interface IShipperRates {
  shipperRatesList: IShipperRate[]
  fetching: boolean
  error?: string
  totalCount: number
  activeStatusCounts: IActiveStatusCounts
  params: IShipperRatesParams
  showModal: boolean
  modalPayload?: IShipperRateModalPayload
  activeTab: SHIPPER_RATE_STATUS
}

export type UpdateAcceptanceCapConfigurationModal = 'updateAcceptanceCapConfigurationModal'

export const ShipperRates = () => {
  const dispatch = useDispatch()
  const { id } = useParams<{ id: string }>()
  const [shipperRateHistoryPanelOpen, setshipperRateHistoryPanelOpen] = useState(!!id)
  const [shipperRate, setShipperRate] = useState<IShipperRate>()

  const {
    activeStatusCounts,
    activeTab,
    error,
    fetching,
    modalPayload,
    params,
    shipperRatesList,
    showModal,
    totalCount,
  } = useSelector<IStore, IShipperRates>(({ modals, shipperRates }) => ({
    shipperRatesList: shipperRates.shipperRates,
    totalCount: shipperRates.totalCount,
    activeStatusCounts: shipperRates.activeStatusCounts,
    fetching: shipperRates.fetching,
    error: shipperRates.error,
    params: shipperRates.params,
    activeTab: shipperRates.activeTab,
    showModal: modals.showEditModal,
    modalPayload: modals.editModalPayload,
  }))

  useEffect(() => {
    dispatch(
      fetchShipperRates({
        ...params,
        pageCount: 0,
        filters: {
          ...params.filters,
          activeStatus: activeTab.toLowerCase(),
        },
      })
    )
  }, [activeTab])

  useEffect(() => {
    if (id) {
      setshipperRateHistoryPanelOpen(true)
    } else {
      setshipperRateHistoryPanelOpen(false)
    }
  }, [id])

  const { closeModal, isOpen, openModal } = useModal<UpdateAcceptanceCapConfigurationModal>()

  const openAcceptanceCapModal = openModal('updateAcceptanceCapConfigurationModal')

  const { clickOverflowTrigger, expanded, setExpanded } = useOverflowToggle()
  const [showBulkModal, setBulkModal] = useState<boolean>(false)
  const [bulkUpdate, setBulkUpdate] = useState<boolean>(false)
  const closeBulkModalHandler = () => {
    setBulkModal(false)
    setBulkUpdate(false)
  }

  const changeTabHandler = (tab: string) => dispatch(setActiveTab(tab as SHIPPER_RATE_STATUS))
  const changePageHandler = (page: number) =>
    dispatch(fetchShipperRates({ ...params, pageCount: page - 1 }))
  const changeSortingHandler = (sortField: SORT_FIELD) =>
    dispatch(
      fetchShipperRates({
        ...params,
        pageCount: 0,
        sortField,
        sortOrder:
          sortField === params.sortField && params.sortOrder === SORT_ORDER.ASC
            ? SORT_ORDER.DESC
            : SORT_ORDER.ASC,
      })
    )
  const openEditModal = () => {
    dispatch(openEditShipperRateModal())
  }

  const addMultipleRatesOption = {
    label: 'Add Multiple Rates',
    itemComponent: (
      <div className='flex flex-row items-center btn-fileinput'>
        <AddIcon size='large' />
        <span className='ml2'>Add Multiple Rates</span>
      </div>
    ),
    action: () => {
      setBulkModal(true)
      setBulkUpdate(false)
    },
  }

  const editMultipleRatesOption = {
    label: 'Update Multiple Rates',
    itemComponent: (
      <div className='flex flex-row items-center'>
        <EditIcon size='large' />
        <span className='ml2'>Update Multiple Rates</span>
      </div>
    ),
    action: () => {
      setBulkModal(true)
      setBulkUpdate(true)
    },
  }

  const openCapModal = () => {
    openAcceptanceCapModal()
  }

  const options: IDropdownOption[] = [addMultipleRatesOption, editMultipleRatesOption]

  const ready = !(fetching || error) || showModal
  return (
    <>
      <Container>
        {!showModal && error && (
          <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={error} />
        )}
        <ShipperRatesFilter activeStatus={activeTab.toLowerCase()} />
        <div className='row'>
          <div className='fr'>
            <OverflowMenu
              options={options}
              trigger={
                <IconButton
                  color='tertiary'
                  label='overflow-trigger'
                  Icon={EllipsisIcon}
                  onClick={clickOverflowTrigger}
                  data-test='overflow-menu'
                  size='large'
                />
              }
              expanded={expanded}
              onExpanded={setExpanded}
              dropdownId='shipper-rate-actions'
            />
          </div>
          <div className='fr'>
            <Button
              color='secondary'
              onClick={openEditModal}
              size='large'
              data-test='add-rate-button'>
              Add New Rate
            </Button>
          </div>
        </div>
        <Tabs
          dataTest='shipper-rate-tabs'
          value={capitalize(activeTab)}
          onChange={changeTabHandler}>
          {$enum(SHIPPER_RATE_STATUS).map(status => (
            <Tab
              key={status}
              label={capitalize(status)}
              dataTest={`${status}-tab`}
              count={activeStatusCounts ? activeStatusCounts[status] : undefined}>
              {fetching && !showModal && <Spinner position='relative' />}
              {ready && (
                <ShipperRatesTable
                  shipperRates={shipperRatesList}
                  totalCount={totalCount}
                  params={params}
                  onPageChange={changePageHandler}
                  onSortingChange={changeSortingHandler}
                  openAcceptanceCapModal={openCapModal}
                  setShipperRate={setShipperRate}
                />
              )}
            </Tab>
          ))}
        </Tabs>
        <ShipperRateHistory
          setSidebarOpen={setshipperRateHistoryPanelOpen}
          sidebarOpen={shipperRateHistoryPanelOpen}
        />
        {isOpen('updateAcceptanceCapConfigurationModal') && (
          <UpdateAcceptanceCapModal onClose={closeModal} shipperRate={shipperRate} />
        )}
        <EditShipperRateModal
          errors={error || ''}
          showModal={showModal}
          rate={modalPayload?.rate}
        />
        <BulkRatesModal
          errors={error || ''}
          showModal={showBulkModal}
          isUpdate={bulkUpdate}
          closeModalHandler={closeBulkModalHandler}
        />
      </Container>
    </>
  )
}
